/* Images a user attaches (the counterparty card's comments): which are taken, and the JPEG each
 * becomes before it is sent.
 *
 * ⚠ These checks are the ONLY limits in the system — the API takes any JPEG or PNG data URI and
 * checks nothing else (count, size, original type). So a file is refused here, with its reason,
 * before anything is read: type first, then size, then the count left.
 *
 * Every accepted file is re-encoded in the browser: the longer side capped at `MAX_SIDE` (a
 * smaller image is never scaled up), JPEG at `JPEG_QUALITY`, drawn over white so a transparent PNG
 * does not turn black. A GIF keeps its first frame only — expected.
 */

export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGES = 5;
const MAX_SIDE = 2000;
const JPEG_QUALITY = 0.85;

/** `files` sorted into the ones taken and the ones refused (`reason`: "type" | "size" | "count"),
    given how many are already attached. */
export function checkImages(files, attached = 0) {
  const accepted = [];
  const rejected = [];
  Array.from(files).forEach(file => {
    if (!IMAGE_TYPES.includes(file.type)) rejected.push({ file, reason: "type" });
    else if (file.size > MAX_IMAGE_BYTES) rejected.push({ file, reason: "size" });
    else if (attached + accepted.length >= MAX_IMAGES) rejected.push({ file, reason: "count" });
    else accepted.push(file);
  });
  return { accepted, rejected };
}

const decode = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode"));
    };
    img.src = url;
  });

/** The file as a JPEG `Blob`: longer side at most `MAX_SIDE`, transparency on white. Rejects when
    the browser cannot decode it. */
export async function toJpeg(file) {
  const img = await decode(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error("encode"))), "image/jpeg", JPEG_QUALITY));
}

/** A `Blob` as a `data:` URI — what the API takes. */
export const blobToDataUri = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
